import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import BlogHero from './components/BlogHero';
import BlogSearch from './components/BlogSearch';
import FeaturedPost from './components/FeaturedPost';
import BlogGrid from './components/BlogGrid';
import BlogPagination from './components/BlogPagination';
import { fetchPosts, fetchBlogFilters } from '../../fetchProjects';
import Footer from '../../components/ui/Footer';

const BlogPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [allPosts, setAllPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);

  // Fetch posts and filters
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [postsData, filtersData] = await Promise.all([
          fetchPosts(),
          fetchBlogFilters()
        ]);
        
        const sorted = [...postsData].sort((a, b) => {
          if (a.isNew === b.isNew) return new Date(b.date) - new Date(a.date);
          return b.isNew - a.isNew;
        });
        
        setAllPosts(sorted);
      } catch (e) {
        console.error('Failed to fetch blog data:', e);
      }
      setLoading(false);
    };
    loadData();
  }, []);

  // Category options for the UI
  const categories = [
    { id: 'all', name: 'All Posts', count: allPosts.length },
    ...Array.from(
      allPosts.reduce((acc, post) => {
        const catName = post.category || 'Uncategorized';
        if (!acc.has(catName)) {
          acc.set(catName, {
            id: catName,
            name: catName,
            count: 1
          });
        } else {
          acc.get(catName).count += 1;
        }
        return acc;
      }, new Map()).values()
    )
  ];

  // Featured post (from database flag or fallback to first)
  const featuredPost = allPosts.find(post => post.featured === 1) || allPosts[0] || null;

  // Filter posts based on search and category
  useEffect(() => {
    setLoading(true);

    setTimeout(() => {
      // Exclude the featured post from the grid
      let filtered = allPosts.filter(post => post.id !== featuredPost?.id);

      // Filter by category
      if (selectedCategory !== 'all') {
        filtered = filtered.filter(post => post.category === selectedCategory);
      }

      // Filter by search term
      if (searchTerm) {
        filtered = filtered.filter(post =>
          post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
          post.category.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }

      setFilteredPosts(filtered);
      setCurrentPage(1);
      setLoading(false);
    }, 300);
  }, [searchTerm, selectedCategory, allPosts]);

  // Pagination
  const postsPerPage = 9;
  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);
  const startIndex = (currentPage - 1) * postsPerPage;
  const currentPosts = filteredPosts.slice(startIndex, startIndex + postsPerPage);

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const handleCategoryFilter = (category) => {
    setSelectedCategory(category);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Dynamically generate JSON-LD schema based on fetched posts
  const seoSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "CollectionPage",
        "@id": "https://sudhan.live/blog#webpage",
        "url": "https://sudhan.live/blog",
        "name": "Blog - Sudhan R",
        "description": "Articles, technical updates, tutorials, and thought leadership pieces by Sudhan R.",
        "isPartOf": { "@id": "https://sudhan.live/#website" },
        "mainEntity": {
          "@type": "Blog",
          "@id": "https://sudhan.live/blog#blog",
          "name": "Sudhan R Blog",
          "description": "Official technical blog and insights of Sudhan R on AI, earth observation, and space tech.",
          "publisher": { "@id": "https://sudhan.live/#person" },
          "inLanguage": "en",
          "blogPost": allPosts.filter(p => p.published !== 0).slice(0, 20).map(post => ({
            "@type": "BlogPosting",
            "@id": `https://sudhan.live/blog/${post.slug || post.id}#blogpost`,
            "headline": post.title,
            "datePublished": post.date,
            "url": `https://sudhan.live/blog/${post.slug || post.id}`,
            "description": post.excerpt || post.description || "",
            "image": post.image,
            "author": {
              "@id": "https://sudhan.live/#person"
            }
          }))
        },
        "breadcrumb": {
          "@id": "https://sudhan.live/blog#breadcrumb"
        }
      },
      {
        "@type": "BreadcrumbList",
        "@id": "https://sudhan.live/blog#breadcrumb",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://sudhan.live/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Blog",
            "item": "https://sudhan.live/blog"
          }
        ]
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>Blog – Professional Insights & Thought Leadership | Sudhan R</title>
        <meta
          name="description"
          content="Explore blog posts from ISRO and NASA STEM experiences, space-tech training, AI projects, and student outreach. Practical advice, technical case studies, and career reflections."
        />
        <meta
          name="keywords"
          content="Sudhan R, ISRO, NASA, blog, remote sensing, AI, astronomy, outreach, training, certification, research fellow, STEM, space research"
        />
        <meta property="og:title" content="Blog – Sudhan R's Insights on Space-Tech & Research" />
        <meta
          property="og:description"
          content="Explore technical blogs and research takeaways from ISRO, NASA, IAAC, and hands-on projects in space, AI, and innovation."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sudhan.live/blog" />
        <meta property="og:image" content="https://sudhan.live/assets/images/Sudhan_2026_v3.png" />

        <link rel="canonical" href="https://sudhan.live/blog" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Blog – Sudhan R's Insights on Space-Tech & Research" />
        <meta name="twitter:description" content="Explore technical blogs and research takeaways from ISRO, NASA, IAAC, and hands-on projects in space, AI, and innovation." />
        <meta name="twitter:image" content="https://sudhan.live/assets/images/Sudhan_2026_v3.png" />

        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(seoSchema)}
        </script>
      </Helmet>


      <div className="min-h-screen bg-transparent">
        <Header />

        {/* Hero Section */}
        <BlogHero />

        {/* Featured Post */}
        <FeaturedPost post={featuredPost} loading={loading} />

        {/* Search and Filters */}
        <BlogSearch
          onSearch={handleSearch}
          onCategoryFilter={handleCategoryFilter}
          selectedCategory={selectedCategory}
          categories={categories}
        />

        {/* Blog Grid */}
        <BlogGrid posts={currentPosts} loading={loading} />

        {/* Pagination */}
        {totalPages > 1 && (
          <BlogPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            totalArticles={filteredPosts.length}
            postsPerPage={postsPerPage}
          />
        )}

        
        <Footer />
      </div>
    </>
  );
};

export default BlogPage;